import Splide from '@splidejs/splide';

/**
 * Product-card colour swatches: Splide carousel with responsive overflow behaviour.
 *
 * Uses Splide overflow API — see https://splidejs.com/guides/overflow/
 *
 * @export
 * @class ProductSwatches
 */
export default class ProductSwatches {
  /**
   * @param {HTMLElement} container - Wrapper with `[data-product-item-swatches]`
   */
  constructor(container) {
    const root = container?.querySelector(
      '[data-product-item-swatches-splide]',
    );
    if (!root) {
      return;
    }

    this.root = root;

    this._syncOverflowOptions = this._syncOverflowOptions.bind(this);
    this._onArrowClick = this._onArrowClick.bind(this);

    const startIndex = this._getSelectedSlideIndex();

    this.splide = new Splide(root, {
      type: 'slide',
      arrows: false,
      pagination: false,
      autoWidth: true,
      drag: false,
      rewind: false,
      start: startIndex,
      /*
       * Without this the index range runs past the last position the track can actually
       * reach. `trimSpace` clamps the translate at the end, so the final few indices all
       * resolve to the same offset: the arrow click registers and `splide.index` moves, but
       * nothing on screen does, and the arrow never disables because Splide still believes
       * a next slide exists. It reads as an arrow that has stopped working, for as many
       * clicks as it takes to walk the index back into reachable range.
       *
       * `omitEnd` clamps the index range itself to the last reachable position, so every
       * click either moves the strip or leaves a properly disabled arrow.
       */
      omitEnd: true,
    });

    /** Must subscribe before {@link Splide.mount} — see Splide overflow guide */
    this.splide.on('overflow', this._syncOverflowOptions);

    this.splide.on('mounted', () => {
      const index = this._getSelectedSlideIndex();
      if (this.splide.index !== index) {
        this.splide.go(index);
      }
    });

    this.splide.mount();

    this._bindArrows();

    requestAnimationFrame(() => this._primeOverflowOnce());
  }

  /**
   * Take the arrow clicks off Splide and navigate by an explicit index.
   *
   * Splide's own `Arrows` component calls `Controller.go('<' | '>')`, and with `autoWidth`
   * the adjacent-index arithmetic goes degenerate once the strip is clamped against its end:
   * `getPrev()` returns the index the carousel is already on, so `go()` navigates nowhere and
   * the arrow is dead for good. Measured on a 17-swatch card parked at the end —
   * `index: 6, getPrev(): 6` — while `go(index - 1)` moved it happily. Dragging was the only
   * way out, because drag never consults that arithmetic.
   *
   * Splide keeps drawing the arrows and managing their `disabled` state; it just no longer
   * decides where a click goes. The listener is on the capture phase so it settles the click
   * before Splide's own bubble-phase handler can act on it.
   */
  _bindArrows() {
    this._arrows = [
      this.root.querySelector('.splide__arrow--prev'),
      this.root.querySelector('.splide__arrow--next'),
    ].filter(Boolean);

    this._arrows.forEach(arrow =>
      arrow.addEventListener('click', this._onArrowClick, true),
    );
  }

  /**
   * @param {MouseEvent} event
   */
  _onArrowClick(event) {
    event.stopImmediatePropagation();
    event.preventDefault();

    if (!this.splide?.Components?.Controller) {
      return;
    }

    const step = event.currentTarget.classList.contains('splide__arrow--prev')
      ? -1
      : 1;
    const end = this.splide.Components.Controller.getEnd();
    const target = Math.min(Math.max(this.splide.index + step, 0), end);

    if (target !== this.splide.index) {
      this.splide.go(target);
    }
  }

  /**
   * Index of the slide for the checked colour swatch (Splide `is-active` slide).
   * @returns {number}
   */
  _getSelectedSlideIndex() {
    const slides = [...this.root.querySelectorAll('.splide__slide')];
    const index = slides.findIndex(
      slide =>
        slide.hasAttribute('data-swatch-selected') ||
        slide.querySelector('[data-product-item-swatch]:checked'),
    );

    return index >= 0 ? index : 0;
  }

  /**
   * @param {boolean} [isOverflow] - Passed from Splide overflow event; omit to read Layout API
   */
  _syncOverflowOptions(isOverflow) {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    const overflow =
      typeof isOverflow === 'boolean'
        ? isOverflow
        : Boolean(this.splide.Components.Layout.isOverflow());

    /*
     * Arrows show at every width while the strip overflows. Splide only keeps their
     * `disabled` state current while the option is on (`Arrows.init` → `update()`), and that
     * state is what hides the arrow at either end (`disabled:invisible` in the snippet).
     */
    this.splide.options = {
      pagination: false,
      arrows: overflow,
      drag: overflow,
    };
  }

  /**
   * If the initial overflow event raced mount, prime options from Layout.
   *
   * The `refresh()` matters most for the Searchspring card. There the carousel is built from
   * inside a layout effect, immediately after Preact swaps the Shopify fragment's markup in
   * and before the browser has laid any of it out — so Splide measures a track that has no
   * width yet, decides nothing overflows, and leaves the arrows inert. Splide only binds
   * arrow clicks and sets their disabled state while `options.arrows` is true
   * (`Arrows.init` → `listen()`), so a wrong measurement here means clicks land on buttons
   * that were never wired up. Dragging the strip forces a recompute, which is why a drag
   * makes the arrows start working — this does that recompute up front instead.
   */
  _primeOverflowOnce() {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    try {
      this.splide.refresh();
      this._syncOverflowOptions(this.splide.Components.Layout.isOverflow?.());

      // `refresh()` re-inits the components without re-emitting `mounted`, so the handler
      // that parks the strip on the checked colour does not run again. Re-assert it here or
      // a card can come back scrolled somewhere else entirely.
      const index = this._getSelectedSlideIndex();
      if (this.splide.index !== index) {
        this.splide.go(index);
      }
    } catch {
      /* noop */
    }
  }

  destroy() {

    (this._arrows || []).forEach(arrow =>
      arrow.removeEventListener('click', this._onArrowClick, true),
    );
    this._arrows = null;

    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }

    this.root = null;
  }
}
